import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useUserData } from '../contexts/UserDataContext';
import Button from '../components/Button';
import Slider from '../components/Slider';
import { calculatePension, calculateYearsToExpectedPension } from '../utils/pensionCalculations';

const SimulationContainer = styled.div`
  min-height: 80vh;
  padding: ${props => props.theme.spacing.xl} 0;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const FormCard = styled.div`
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.lg};
`;

const FormTitle = styled.h1`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.darkBlue};
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.blue};
  border-bottom: 2px solid ${props => props.theme.colors.orange};
  padding-bottom: ${props => props.theme.spacing.sm};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: 600;
  color: ${props => props.theme.colors.darkGray};
  
  &.required::after {
    content: ' *';
    color: ${props => props.theme.colors.red};
  }
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.gray};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  transition: border-color 0.3s ease;
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  color: ${props => props.theme.colors.darkGray};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.blue};
  }
  
  &:invalid {
    border-color: ${props => props.theme.colors.red};
  }
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 2px solid ${props => props.theme.colors.gray};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: 1rem;
  background: ${props => props.theme.colors.cardBackground || props.theme.colors.white};
  color: ${props => props.theme.colors.darkGray};
  cursor: pointer;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.blue};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  margin: ${props => props.theme.spacing.md} 0;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  font-weight: 500;
  color: ${props => props.theme.colors.darkGray};
`;

const InfoBox = styled.div`
  background: ${props => props.theme.colors.lightGray};
  border-left: 4px solid ${props => props.theme.colors.blue};
  padding: ${props => props.theme.spacing.md};
  border-radius: 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md} 0;
  margin: ${props => props.theme.spacing.md} 0;
  
  p {
    margin: 0;
    font-size: 0.9rem;
    color: ${props => props.theme.colors.darkGray};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const SimulationPage: React.FC = () => {
  const { userData, setUserData, setPensionResult } = useUserData();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const prevAgeRef = useRef(userData.age);
  const prevGenderRef = useRef(userData.gender);

  const currentYear = new Date().getFullYear();
  const retirementAge = userData.gender === 'male' ? 65 : 60;
  const defaultWorkEndYear = currentYear + (retirementAge - userData.age);

  useEffect(() => {
    if (prevAgeRef.current !== userData.age || prevGenderRef.current !== userData.gender) {
      prevAgeRef.current = userData.age;
      prevGenderRef.current = userData.gender;
      setUserData({ workEndYear: defaultWorkEndYear });
    }
  }, [userData.age, userData.gender, defaultWorkEndYear, setUserData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (userData.age < 18 || userData.age > 100) {
      newErrors.age = 'Wiek musi być między 18 a 100 lat';
    }

    if (userData.grossSalary <= 0) {
      newErrors.grossSalary = 'Wynagrodzenie musi być większe od 0';
    }

    if (userData.workStartYear < 1950 || userData.workStartYear > currentYear) {
      newErrors.workStartYear = 'Rok rozpoczęcia pracy jest nieprawidłowy';
    }

    if (userData.workEndYear < userData.workStartYear) {
      newErrors.workEndYear = 'Rok zakończenia pracy musi być po roku rozpoczęcia';
    }

    if (userData.accumulatedFunds && userData.accumulatedFunds < 0) {
      newErrors.accumulatedFunds = 'Zgromadzone środki nie mogą być ujemne';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof typeof userData, value: any) => {
    setUserData({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Calculate pension
      const result = calculatePension(userData);

      // Calculate years needed to reach expected pension if applicable
      const yearsToExpectedPension = userData.expectedPension > 0
        ? calculateYearsToExpectedPension(userData.expectedPension, userData)
        : undefined;

      setPensionResult({
        ...result,
        yearsToExpectedPension: yearsToExpectedPension !== null ? yearsToExpectedPension : undefined
      });
      navigate('/results');
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  return (
    <SimulationContainer>
      <Container>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <FormCard>
            <FormTitle>Symulacja przyszłej emerytury</FormTitle>
            
            <form onSubmit={handleSubmit}>
              <FormSection>
                <SectionTitle>Dane podstawowe</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Slider
                      label="Wiek"
                      value={userData.age}
                      onChange={(value) => handleInputChange('age', value)}
                      min={18}
                      max={100}
                      unit=" lat"
                    />
                    {errors.age && <span style={{ color: 'red', fontSize: '0.9rem' }}>{errors.age}</span>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="gender" className="required">
                      Płeć
                    </Label>
                    <Select
                      id="gender"
                      value={userData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                      required
                    >
                      <option value="male">Mężczyzna</option>
                      <option value="female">Kobieta</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Slider
                      label="Wynagrodzenie brutto"
                      value={userData.grossSalary}
                      onChange={(value) => handleInputChange('grossSalary', value)}
                      min={1000}
                      max={50000}
                      step={100}
                      unit=" PLN"
                    />
                    {errors.grossSalary && <span style={{ color: 'red', fontSize: '0.9rem' }}>{errors.grossSalary}</span>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="workStartYear" className="required">
                      Rok rozpoczęcia pracy
                    </Label>
                    <Input
                      id="workStartYear"
                      type="number"
                      value={userData.workStartYear}
                      onChange={(e) => handleInputChange('workStartYear', parseInt(e.target.value) || 0)}
                      min="1950"
                      max={currentYear}
                      required
                    />
                    {errors.workStartYear && <span style={{ color: 'red', fontSize: '0.9rem' }}>{errors.workStartYear}</span>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="workEndYear" className="required">
                      Planowany rok zakończenia pracy
                    </Label>
                    <Input
                      id="workEndYear"
                      type="number"
                      value={userData.workEndYear}
                      onChange={(e) => handleInputChange('workEndYear', parseInt(e.target.value) || 0)}
                      min={userData.workStartYear}
                      required
                    />
                    {errors.workEndYear && <span style={{ color: 'red', fontSize: '0.9rem' }}>{errors.workEndYear}</span>}
                  </FormGroup>
                </FormGrid>
              </FormSection>

              <FormSection>
                <SectionTitle>Dane dodatkowe (opcjonalne)</SectionTitle>
                
                <FormGroup>
                  <Slider
                    label="Zgromadzone środki na koncie i subkoncie"
                    value={userData.accumulatedFunds || 0}
                    onChange={(value) => handleInputChange('accumulatedFunds', value)}
                    min={0}
                    max={500000}
                    step={1000}
                    unit=" PLN"
                  />
                  <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                    Jeśli nie znasz, zostaw 0 - zostanie oszacowane
                  </p>
                  {errors.accumulatedFunds && <span style={{ color: 'red', fontSize: '0.9rem' }}>{errors.accumulatedFunds}</span>}
                </FormGroup>

                <CheckboxGroup>
                  <Checkbox
                    id="includeSickLeave"
                    type="checkbox"
                    checked={userData.includeSickLeave}
                    onChange={(e) => handleInputChange('includeSickLeave', e.target.checked)}
                  />
                  <CheckboxLabel htmlFor="includeSickLeave">
                    Uwzględnij możliwość zwolnień lekarskich
                  </CheckboxLabel>
                </CheckboxGroup>

                {userData.includeSickLeave && (
                  <InfoBox>
                    <p>
                      <strong>Informacja o zwolnieniach lekarskich:</strong><br />
                      Średnio pracujący w Polsce przebywa na zwolnieniach lekarskich około 12 dni w roku. 
                      To może obniżyć wysokość przyszłej emerytury o około 3-5%.
                    </p>
                  </InfoBox>
                )}
              </FormSection>

              <ButtonGroup>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                >
                  Wróć
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                >
                  Prognozuj moją przyszłą emeryturę
                </Button>
              </ButtonGroup>
            </form>
          </FormCard>
        </motion.div>
      </Container>
    </SimulationContainer>
  );
};

export default SimulationPage;
